import {
  useConsumerMetaData,
  useConsumerSearch,
  useCreatePayment,
  useInstallments,
  useLatestConsumerBill,
  usePaymentChannelChoices,
  usePaymentMethod,
  usePaymentPayTypeChoices,
  usePaymentServices,
  useServiceOrder,
} from "@features/cx/payments/hooks";
import {
  ConsumerSearchResult,
  CreatePaymentPayload,
  Installment,
  LatestConsumerBill,
  PaymentServices,
} from "@features/cx/payments/types";
import { DynamicForm } from "@shared/components/DynamicForm";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { ContextualHelp } from "@shared/ui/contextual-help";
import { Input } from "@shared/ui/input";
import { Label } from "@shared/ui/label";
import { Separator } from "@shared/ui/separator";
import {formatTerritoryAddress} from "@shared/utils/address";
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Mail,
  Phone,
  Search,
  Shield,
  User,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";

// Import the custom field utilities
import { PaymentMethod } from "@features/cx/shared/types/consumerRequest";
import { logEvent } from "@shared/analytics/analytics";
import { toast } from "@shared/hooks/use-toast";
import { Badge } from "@shared/ui/badge";
import {
  CustomFieldConfig,
  useCustomFieldsForm,
} from "@shared/utils/customFormMaker";
import { useNavigate, useParams } from "react-router-dom";

export function CreatePayment() {
  const { consumerId } = useParams(); // Get consumerId from URL
  const cachedUserProfile = localStorage.getItem("cached_user_profile");
  const userProfile = cachedUserProfile ? JSON.parse(cachedUserProfile) : null;
  console.log("userProfile", userProfile);
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedAccount, setSelectedAccount] =
    useState<ConsumerSearchResult | null>(null);
  const [paymentData, setPaymentData] = useState<any>({});
  const [createCreditNote, setCreateCreditNote] = useState<boolean>(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<PaymentMethod | null>(null);
  const [balanceAmount, setBalanceAmount] = useState<number>(0);
  const isUpdatingAmount = useRef(false);

  const remoteUtilityId = Number(useRemoteUtilityId());

  // Determine which consumer ID to use - from URL or selected account
  const activeConsumerId = consumerId
    ? Number(consumerId)
    : selectedAccount?.id || 0;

  // API Hooks
  const consumerSearchQuery = useConsumerSearch({
    searchData: searchQuery,
    remoteUtilityId: remoteUtilityId || 0,
  });

  // Updated to use activeConsumerId which can come from URL or selected account
  const consumerMetaDataQuery = useConsumerMetaData({
    consumerId: activeConsumerId,
    remoteUtilityId: remoteUtilityId || 0,
    enabled: !!activeConsumerId, // Run when we have either URL param or selected account
  });

  const paymentPayTypeChoicesQuery = usePaymentPayTypeChoices(
    remoteUtilityId || 0
  );
  const paymentChannelChoicesQuery = usePaymentChannelChoices(
    remoteUtilityId || 0
  );

  // Updated to use activeConsumerId and handle consumerNo from metadata when coming from URL
  const createPaymentMutation = useCreatePayment(
    remoteUtilityId || 0,
    activeConsumerId,
    selectedAccount?.consumerNo ||
      consumerMetaDataQuery.data?.result?.consumerNo ||
      ""
  );

  // Conditional API calls based on payment type and active consumer
  const latestBillQuery = useLatestConsumerBill({
    remoteUtilityId: remoteUtilityId || 0,
    consumerId: activeConsumerId,
    consumerNo:
      selectedAccount?.consumerNo ||
      consumerMetaDataQuery.data?.result?.consumerNo ||
      "",
  });

  const installmentsQuery = useInstallments({
    remoteUtilityId: remoteUtilityId || 0,
    consumerId: activeConsumerId,
    consumerNo:
      selectedAccount?.consumerNo ||
      consumerMetaDataQuery.data?.result?.consumerNo ||
      "",
  });

  console.log("Selected Account:", selectedAccount);
  const paymentServicesQuery = usePaymentServices({
    remoteUtilityId: remoteUtilityId || 0,
    consumerId: activeConsumerId,
  });

  const paymentMethodQuery = usePaymentMethod({
    remoteUtilityId: remoteUtilityId || 0,
  });

  const { data: serviceOrderPaymentData } = useServiceOrder({
    remote_utility_id: remoteUtilityId,
    consumer_id: activeConsumerId,
  });

  const serviceOrderPaymentOptions =
    serviceOrderPaymentData?.result?.map((item: any) => ({
      value: item.id,
      label: item.sopName,
      amount: item.totalActualCost,
    })) || [];

  console.log("sopppppppppp", serviceOrderPaymentOptions);
  // Effect to handle URL parameter - create a mock selectedAccount from metadata
  useEffect(() => {
    if (consumerId && consumerMetaDataQuery.data?.result && !selectedAccount) {
      const metadata = consumerMetaDataQuery.data.result;

      // Create a mock selectedAccount object from metadata to maintain compatibility
      const mockSelectedAccount: ConsumerSearchResult = {
        id: metadata.id,
        firstName: metadata.firstName,
        lastName: metadata.lastName,
        consumerNo: metadata.consumerNo,
        email: metadata.email,
        contactNumber: metadata.contactNumber,
        statusDisplay: metadata.statusDisplay || "ACTIVE",
        // Add missing required fields with fallback values
        status: metadata.status || "ACTIVE",
        serviceAddress: metadata.serviceAddress,
        phone: metadata.contactNumber || "",
        outstandingBalance: metadata.outstandingBalance || 0,
      };

      setSelectedAccount(mockSelectedAccount);
    }
  }, [consumerId, consumerMetaDataQuery.data, selectedAccount]);

  // Get data from API or use fallback
  const consumers = Array.isArray(consumerSearchQuery.data?.result)
    ? consumerSearchQuery.data.result
    : [];
  const consumerMetadata = consumerMetaDataQuery.data?.result;
console.log('consumerMetadata',consumerMetadata)
  const services: PaymentServices[] = paymentServicesQuery.data?.result
    ? Array.isArray(paymentServicesQuery.data.result)
      ? paymentServicesQuery.data.result
      : [paymentServicesQuery.data.result]
    : [];

  // Convert API response to camelCase and proper types
  const pendingBills: LatestConsumerBill[] = latestBillQuery.data?.result
    ? Array.isArray(latestBillQuery.data.result)
      ? latestBillQuery.data.result
      : [latestBillQuery.data.result]
    : [];

  const installments: Installment[] = installmentsQuery.data?.result
    ? Array.isArray(installmentsQuery.data.result)
      ? installmentsQuery.data.result
      : [installmentsQuery.data.result]
    : [];

  const paymentChannels = paymentChannelChoicesQuery.data?.result
    ? Array.isArray(paymentChannelChoicesQuery.data.result)
      ? paymentChannelChoicesQuery.data.result
      : [paymentChannelChoicesQuery.data.result]
    : [];

  const paymentPayTypes = paymentPayTypeChoicesQuery.data?.result
    ? Array.isArray(paymentPayTypeChoicesQuery.data.result)
      ? paymentPayTypeChoicesQuery.data.result
      : [paymentPayTypeChoicesQuery.data.result]
    : [];

  const paymentMethods = paymentMethodQuery.data?.result
    ? Array.isArray(paymentMethodQuery.data.result)
      ? paymentMethodQuery.data.result
      : [paymentMethodQuery.data.result]
    : [];

  // Custom field configuration for payment methods - memoized to prevent re-creation
  const customFieldConfig = useMemo<CustomFieldConfig>(
    () => ({
      namingStrategy: "key-fallback",
      classes: {
        container: "w-full mb-6",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input:
          "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
      gridColumns: 3,
      responsiveColumns: { sm: 1, md: 2, lg: 3 },
      fullWidth: true,
      enableTooltips: true,
      fieldNameCleaning: true,
    }),
    []
  ); // Empty dependency array since this config never changes
  // Create custom fields form for payment method
  const customFieldsForm = useForm({
    mode: "onBlur",
  });

  // Use the utility hook for payment method custom fields
  const {
    formFields: paymentMethodCustomFields,
    defaultValues: paymentMethodDefaults,
    resetFormWithDefaults,
    hasFields: hasPaymentMethodFields,
  } = useCustomFieldsForm(
    Array.isArray(selectedPaymentMethod?.customFields)
      ? selectedPaymentMethod.customFields
      : undefined,
    customFieldsForm,
    customFieldConfig
  );

  // Reset custom fields form when payment method changes
  useEffect(() => {
    if (
      selectedPaymentMethod &&
      Array.isArray(selectedPaymentMethod.customFields)
    ) {
      resetFormWithDefaults();
    }
  }, [selectedPaymentMethod?.code, resetFormWithDefaults]); // Only depend on the code, not the whole object
  // Debug logging to see what data we're receiving
  useEffect(() => {
    if (latestBillQuery.data) {
      console.log("Latest Bill Query Data:", latestBillQuery.data);
      console.log("Raw Result:", latestBillQuery.data.result);
      console.log("Is Array:", Array.isArray(latestBillQuery.data.result));
      console.log("Processed Pending Bills:", pendingBills);
      console.log("Pending Bills Length:", pendingBills.length);
    }
    if (paymentServicesQuery.data) {
      console.log("Payment Services Query Data:", paymentServicesQuery.data);
      console.log("Processed Services:", services);
    }
  }, [latestBillQuery.data, pendingBills, paymentServicesQuery.data, services]);

  // Filter consumers for search results
  const filteredConsumers = Array.isArray(consumers)
    ? consumers.filter(
        (consumer: ConsumerSearchResult) =>
          consumer.firstName
            ?.toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          consumer.lastName
            ?.toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          consumer.consumerNo?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [];

  // Create form fields using FormService
  const formFields: FormField[] = useMemo(
    () => [
      // Payment Details Group - Green
      {
        name: "paymentChannel",
        label: "Payment Channel",
        type: "select",
        required: true,
        group: "Payment & Type Selection",
        groupColor: "border-l-green-500",
        groupOrder: 1,
        placeholder: "Select payment channel",
        options: (Array.isArray(paymentChannels) ? paymentChannels : []).map(
          (channel) => ({
            value: channel.code,
            label: channel.name,
            description: `Processing Fee: $${channel.processingFee}`,
          })
        ),
        helperText: "Select how the payment is being received",
        showHelperTooltip: true,
        tooltipText:
          "Different payment channels may have different processing times and fees.",
        fullWidth: true,
        classes: {
          container: "w-full mb-6",
          label: "block text-sm font-medium text-gray-700 mb-2",
          select:
            "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-xs text-gray-500 mt-1",
        },
      },
      {
        name: "paymentType",
        label: "Payment Type",
        type: "select",
        required: true,
        group: "Payment & Type Selection",
        groupColor: "border-l-green-500",
        groupOrder: 2,
        placeholder: "Select payment type",
        options: (Array.isArray(paymentPayTypes) ? paymentPayTypes : []).map(
          (payType) => ({
            value: payType.key,
            label: payType.value,
          })
        ),
        defaultValue: "0",
        helperText: "Choose the type of payment you want to process",
        showHelperTooltip: true,
        tooltipText:
          "Bill Payment: Pay outstanding utility bills and fees. Service Payment: Purchase additional services like water testing. Installments: Pay scheduled installment payments.",
        fullWidth: true,
        classes: {
          container: "w-full mb-6",
          label: "block text-sm font-medium text-gray-700 mb-2",
          select:
            "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-xs text-gray-500 mt-1",
        },
      },

      // Bill Selection - Shows when paymentType is billPayment
      {
        name: "selectedBills",
        label: "Select Bills to Pay",
        type: "multi-select",
        group: "Payment & Type Selection",
        groupColor: "border-l-green-500",
        groupOrder: 3,
        options: (() => {
          // Explicitly check for null, undefined, and empty array
          if (
            !pendingBills ||
            !Array.isArray(pendingBills) ||
            pendingBills.length === 0
          ) {
            return [];
          }

          return pendingBills
            .map((bill) => {
              // Additional safety check for each bill object
              if (!bill || !bill.billId) {
                return null;
              }

              return {
                value: bill.billId,
                label: `${bill.billMonth || "Unknown"} - $${
                  bill.outstandingBalance || "0"
                }`,
                description: `Due: ${bill.dueDate || "Unknown"}`,
              };
            })
            .filter(Boolean); // Remove any null entries
        })(),
        showWhen: (formValues) => formValues.paymentType === "2",
        helperText: "Select one or more bills to pay",
        showHelperTooltip: true,
        tooltipText:
          "Choose which outstanding bills you want to pay. You can select multiple bills.",
        fullWidth: true,
        classes: {
          container: "w-full mb-6",
          label: "block text-sm font-medium text-gray-700 mb-2",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-xs text-gray-500 mt-1",
        },
      },
      // Service Selection - Shows when paymentType is servicePayment
      {
        name: "selectedServices",
        label: "Select Services",
        type: "multi-select",
        group: "Payment & Type Selection",
        groupColor: "border-l-green-500",
        groupOrder: 2,
        options: (Array.isArray(services) ? services : []).map(
          (service: PaymentServices) => ({
            value: service.id,
            label: `${service.utilitySupportName} - $${service.serviceCharge}`,
            description: `Service charge: $${service.serviceCharge}`,
          })
        ),
        showWhen: (formValues: any) => formValues.paymentType === "5",
        helperText: "Select one or more services to purchase",
        showHelperTooltip: true,
        tooltipText: "Choose which additional services you want to purchase.",
        fullWidth: true,
        classes: {
          container: "w-full mb-6",
          label: "block text-sm font-medium text-gray-700 mb-2",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-xs text-gray-500 mt-1",
        },
      },

      // Installment Selection - Shows when paymentType is installments
      {
        name: "selectedInstallments",
        label: "Select Installments",
        type: "multi-select",
        group: "Payment & Type Selection",
        groupColor: "border-l-green-500",
        groupOrder: 4,
        options: (Array.isArray(installments) ? installments : []).map(
          (installment: Installment) => ({
            value: installment.id,
            label: `${installment.installmentNo} - $${installment.installmentAmount}`,
            description: `Due: ${installment.status}`,
          })
        ),
        showWhen: (formValues: any) => formValues.paymentType === "6",
        helperText: "Select one or more installments to pay",
        showHelperTooltip: true,
        tooltipText: "Choose which installment payments you want to make.",
        fullWidth: true,
        classes: {
          container: "w-full mb-6",
          label: "block text-sm font-medium text-gray-700 mb-2",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-xs text-gray-500 mt-1",
        },
      },

      {
        name: "selectedServiceOrderPayment",
        label: "Select Service Order Payment",
        type: "select",
        group: "Payment & Type Selection",
        groupColor: "border-l-green-500",
        groupOrder: 4,
        options: serviceOrderPaymentOptions,
        showWhen: (formValues: any) => formValues.paymentType === "7",
        helperText: "Select service order to pay",
        showHelperTooltip: true,
        tooltipText: "Choose which service order payment you want to make.",
        fullWidth: true,
        classes: {
          container: "w-full mb-6",
          label: "block text-sm font-medium text-gray-700 mb-2",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-xs text-gray-500 mt-1",
        },
      },

      // Payment Amount
      {
        name: "expectedAmount",
        label: "Payment Amount",
        type: "decimal",
        required: false,
        group: "Payment & Type Selection",
        groupColor: "border-l-green-500",
        groupOrder: 5,
        placeholder: "0.00",
        disabled: true,
        min: 0,
        prefix: "$",
        helperText: "Total amount to be paid",
        showHelperTooltip: true,
        tooltipText:
          "This amount will be automatically calculated based on your selections above.",
        fullWidth: true,
        classes: {
          container: "w-full mb-6",
          label: "block text-sm font-medium text-gray-700 mb-2",
          input:
            "w-full h-12 px-4 pl-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-xs text-gray-500 mt-1",
        },
      },

      // Payment Method Group - Yellow
      {
        name: "paymentMethod",
        label: "Payment Mode",
        type: "select",
        required: true,
        group: "Payment Mode",
        groupColor: "border-l-yellow-500",
        groupOrder: 3,
        placeholder: "Select payment method",
        options: (Array.isArray(paymentMethods) ? paymentMethods : []).map(
          (method: PaymentMethod) => ({
            value: method.code,
            label: method.name,
            description: method.remark || `${method.name} payment`,
          })
        ),
        helperText: "Select how the customer wants to pay",
        showHelperTooltip: true,
        tooltipText:
          "Different methods may have different processing times and fees. Cash payments are processed immediately.",
        fullWidth: true,
        classes: {
          container: "w-full mb-6 col-span-2",
          label: "block text-sm font-medium text-gray-700 mb-2",
          select:
            "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-xs text-gray-500 mt-1",
        },
      },

      // Dynamic Payment Method Custom Fields - Same Group
      ...(selectedPaymentMethod &&
      Array.isArray(selectedPaymentMethod.customFields)
        ? paymentMethodCustomFields.map((field) => ({
            ...field,
            group: "Payment Mode",
            groupColor: "border-l-yellow-500",
            groupOrder: 3,
            showWhen: (formValues: any) =>
              formValues.paymentMethod === selectedPaymentMethod.code,
            disabled: field.disabled || field.label?.toLowerCase() === "amount",
          }))
        : []),
      // Additional Notes
      {
        name: "note",
        label: "Additional Notes",
        type: "textarea",
        group: "Payment Mode",
        groupOrder: 3,
        placeholder: "Enter any additional notes (optional)",
        helperText: "Optional notes for internal reference",
        fullWidth: true,
        classes: {
          container: "w-full mb-6 col-span-2",
          label: "block text-sm font-medium text-gray-700 mb-2",
          input:
            "w-full min-h-20 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-xs text-gray-500 mt-1",
        },
      },
    ],
    [
      paymentChannels,
      pendingBills,
      services,
      installments,
      paymentMethods,
      serviceOrderPaymentOptions,
      selectedPaymentMethod?.code, // Only depend on the code, not the whole object
      paymentMethodCustomFields.length, // Only depend on length
      latestBillQuery.dataUpdatedAt,
      paymentServicesQuery.dataUpdatedAt,
      paymentPayTypeChoicesQuery.dataUpdatedAt,
      serviceOrderPaymentData?.dataUpdatedAt,
    ]
  );

  // Create form using FormService
  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    defaultValues: {
      paymentChannel: "walk-in",
      paymentType: "billPayment",
      paymentMethod: "cash",
      amount: 0,
      selectedBills: [],
      selectedServices: [],
      selectedInstallments: [],
      receivedCash: 0,
      cardDetails: "",
      note: "",
    },
  });

  // Watch form values for calculations
  const formValues = form.watch();

  // Update selected payment method when form value changes
  useEffect(() => {
    if (formValues.paymentMethod && paymentMethods.length > 0) {
      const method = paymentMethods.find(
        (m) => m.code === formValues.paymentMethod
      );
      if (method && method.code !== selectedPaymentMethod?.code) {
        setSelectedPaymentMethod(method || null);
      }
    }
  }, [formValues.paymentMethod, paymentMethods, selectedPaymentMethod?.code]);

  // Reset form when selected account changes (but not when coming from URL)
  useEffect(() => {
    if (selectedAccount && !consumerId) {
      // Only reset when account is selected manually, not from URL
      form.reset({
        paymentChannel: "walk-in",
        paymentType: "billPayment",
        paymentMethod: "cash",
        amount: 0,
        selectedBills: [],
        selectedServices: [],
        selectedInstallments: [],
        receivedCash: 0,
        cardDetails: "",
        note: "",
      });
      // Reset balance amount when account changes
      setBalanceAmount(0);
    }
  }, [selectedAccount, form, consumerId]);

  const handleAccountSelect = (account: ConsumerSearchResult) => {
    setSelectedAccount(account);
    setSearchQuery(""); // Clear search to hide other results
  };

  // Calculate total amount based on selections
  const calculateAmount = useCallback(
    (values: any) => {
      let total = 0;

      if (
        values.paymentType === "2" &&
        values.selectedBills?.length > 0 &&
        Array.isArray(pendingBills)
      ) {
        values.selectedBills.forEach((billId: number) => {
          const bill = pendingBills.find(
            (b: LatestConsumerBill) => b.billId === billId
          );
          if (bill) total += Number(bill.outstandingBalance);
        });
      } else if (
        values.paymentType === "5" &&
        values.selectedServices?.length > 0 &&
        Array.isArray(services)
      ) {
        values.selectedServices.forEach((serviceId: number) => {
          const service = services.find(
            (s: PaymentServices) => s.id === serviceId
          );
          if (service) total += Number(service.serviceCharge);
        });
      } else if (
        values.paymentType === "6" &&
        values.selectedInstallments?.length > 0 &&
        Array.isArray(installments)
      ) {
        values.selectedInstallments.forEach((installmentId: number) => {
          const installment = installments.find(
            (i: Installment) => i.id === installmentId
          );
          if (installment) total += Number(installment.installmentAmount);
        });
      } else if (
        values.paymentType === "7" &&
        values.selectedServiceOrderPayment &&
        serviceOrderPaymentData?.result
      ) {
        const selectedServiceOrder = serviceOrderPaymentData.result.find(
          (sop: any) => sop.id === Number(values.selectedServiceOrderPayment)
        );
        if (selectedServiceOrder) {
          total += Number(selectedServiceOrder.totalActualCost);
        }
      }

      return parseFloat(total.toFixed(2)); // 👈 force numeric with 2 decimals
    },
    [pendingBills, services, installments, serviceOrderPaymentData?.result]
  );

  // Update amount and balance when selections change
  const handleFieldChange = useCallback(
    (fieldName: string, value: any) => {
      // Prevent recursive calls when updating amount
      if (isUpdatingAmount.current) {
        return;
      }

      if (
        [
          "selectedBills",
          "selectedServices",
          "selectedInstallments",
          "selectedServiceOrderPayment",
          "paymentType",
        ].includes(fieldName)
      ) {
        const newAmount = calculateAmount({
          ...formValues,
          [fieldName]: value,
        });
        console.log("newAmount", newAmount);
        const newBalanceAmount = newAmount;

        if (newAmount !== formValues.expectedAmount) {
          isUpdatingAmount.current = true;
          form.setValue("expectedAmount", newAmount);
          form.setValue("amount", newAmount);
          setBalanceAmount(newBalanceAmount);
          // Reset the flag after a short delay to allow the form to update
          setTimeout(() => {
            isUpdatingAmount.current = false;
          }, 0);
        }
      }

      // Handle custom field changes for payment methods
      if (selectedPaymentMethod && hasPaymentMethodFields) {
        const customFieldNames = paymentMethodCustomFields.map(
          (field) => field.name
        );
        if (customFieldNames.includes(fieldName)) {
          // Update payment data with custom field values
          setPaymentData((prev) => ({
            ...prev,
            [fieldName]: value,
          }));
        }
      }
    },
    [
      formValues,
      form,
      calculateAmount,
      selectedPaymentMethod,
      hasPaymentMethodFields,
      paymentMethodCustomFields,
    ]
  );

  const getBalanceStatus = () => {
    const outstanding = getOutstandingAmount();
    const excess = getExcessAmount();
    const change = getChangeAmount();

    if (outstanding > 0) return "remaining";
    if (excess > 0 && selectedPaymentMethod?.name.toLowerCase() === "cash")
      return "excess";
    if (change > 0) return "change";
    return "perfect";
  };

  // Updated calculation functions to use balanceAmount instead of outstanding balance
  const getOutstandingAmount = () => {
    return Math.max(0, balanceAmount - (Number(formValues.amount) || 0));
  };

  const getExcessAmount = () => {
    return Math.max(0, (Number(formValues.amount) || 0) - balanceAmount);
  };

  const getChangeAmount = () => {
    if (
      selectedPaymentMethod?.name.toLowerCase() === "cash" &&
      formValues.payment_amount
    ) {
      const receivedCash = parseFloat(formValues.payment_amount) || 0;

      return Math.max(0, receivedCash - (Number(formValues.amount) || 0));
    }
    return 0;
  };

  const handleSubmit = async (data: any) => {
    if (!selectedAccount) {
      toast({
        title: "Please select a customer account",
        variant: "destructive",
      });
      return;
    }

    // Check: for cash payments, received cash >= total amount
    if (
      (selectedPaymentMethod?.name.toLowerCase() === "cash" ||
        selectedPaymentMethod?.name.toLowerCase() === "cheque") &&
      Number(data.payment_amount || 0) < Number(data.expectedAmount || 0)
    ) {
      toast({
        title: "Invalid Cash Payment",
        description:
          "Received cash must be equal to or greater than the total amount.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Collect custom fields data from the main form (since custom fields are now part of it)
      let customFieldsData = {};
      if (hasPaymentMethodFields && selectedPaymentMethod) {
        const customFieldNames = paymentMethodCustomFields.map(
          (field) => field.name
        );
        customFieldNames.forEach((fieldName) => {
          if (data[fieldName] !== undefined) {
            customFieldsData[fieldName] = data[fieldName];
          }
        });
      }

      // Prepare payment payload
      const paymentPayload: CreatePaymentPayload = {
        remoteUtilityId: remoteUtilityId,
        consumer: selectedAccount.id,
        amount: data.expectedAmount,
        paymentPayType: data.paymentType,
        paymentMode: data.paymentMethod,
        paymentChannel: data.paymentChannel,
        paymentDate: data.payment_date
          ? data.payment_date
          : new Date().toISOString(),
        status: "CREDIT",
        source: 1,
        paymentReceivedStatus: 0,
        createCreditNote: createCreditNote,
        extraData: {
          additionalNotes: data.note,
          bill_amount: data.amount,
          payment_amount: data.payment_amount,
          excess_refund: getChangeAmount(),
          ...customFieldsData,
          ...paymentData,
        },
      };
      console.log("Payment Payload:", paymentPayload);

      switch (data.paymentType) {
        case "2":
          paymentPayload.remoteBillId = data.selectedBills[0];
          break;
        case "5":
          paymentPayload.consumerSupportRequest = data.selectedServices[0];
          break;
        case "6":
          paymentPayload.paymentInstallment = data.selectedInstallments[0];
          break;
        case "7":
          paymentPayload.remoteSoId = data.selectedServiceOrderPayment;
          paymentPayload.serviceOrderPayment =
            data.selectedServiceOrderPayment[0];
          break;
        //  case "7":
        //   paymentPayload.serviceOrderPayment = data.selectedServiceOrderPayment[0];
        //   paymentPayload.remoteSoID=data.selectedServiceOrderPayment[0]
        //   break;
        default:
          break;
      }

      // Create payment
      const response = await createPaymentMutation.mutateAsync(paymentPayload, {
        onSuccess: () => {
          logEvent("Payment Processed", {
            consumer: selectedAccount.id,
            amount: data.expectedAmount,
            paymentPayType: data.paymentType,
            paymentMode: data.paymentMethod,
            paymentChannel: data.paymentChannel,
            paymentDate: data.payment_date
              ? data.payment_date
              : new Date().toISOString(),
          });
        },
      });

      // Navigate to success page with customer data
      navigate("/cx/payments/success", {
        state: {
          ...response,
          formData: {
            ...data,
            // Add customer information
            firstName: selectedAccount.firstName,
            lastName: selectedAccount.lastName,
            consumerNo: selectedAccount.consumerNo,
            customerInfo: {
              firstName: selectedAccount.firstName,
              lastName: selectedAccount.lastName,
              email: selectedAccount.email,
              contactNumber: selectedAccount.contactNumber,
              consumerNo: selectedAccount.consumerNo,
            },
          },
        },
      });
    } catch (error) {
      console.error("Payment creation failed:", error);
      toast({
        title: "Failed to process payment. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Determine if we should show the search section
  const shouldShowSearch = !consumerId && !selectedAccount;

  // Show loading state when loading consumer from URL
  if (consumerId && consumerMetaDataQuery.isLoading && !selectedAccount) {
    return (
      <div className="space-y-6">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-8 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
            <p className="text-lg font-medium">Loading consumer details...</p>
            <p className="text-sm text-muted-foreground">
              Fetching information for consumer ID: {consumerId}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show error state if consumer from URL is not found
  if (consumerId && consumerMetaDataQuery.isError && !selectedAccount) {
    return (
      <div className="space-y-6">
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="h-8 w-8 text-red-500 mx-auto mb-4" />
            <p className="text-lg font-medium text-red-600">
              Consumer Not Found
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              Unable to find consumer with ID: {consumerId}
            </p>
            <Button
              variant="outline"
              onClick={() => navigate("/cx/payments/create")}
            >
              <Search className="mr-2 h-4 w-4" />
              Search for Different Consumer
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Account Identification Section - Only show if no URL param or no selected account */}
      {shouldShowSearch && (
        <Card className="border-l-4 border-l-blue-500">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Account Identification
              <ContextualHelp
                title="Account Search"
                content="Search for the customer account using their account number, name, or address. You can search by typing any part of their information."
              />
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="search">Search Customer Account</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  id="search"
                  placeholder="Enter account number or name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Search by account number (e.g., WU-10001) or customer name
              </p>
            </div>

            {searchQuery && filteredConsumers.length > 0 && (
              <div className="border rounded-md divide-y max-h-60 overflow-y-auto">
                {filteredConsumers.map((consumer: ConsumerSearchResult) => (
                  <div
                    key={consumer.id}
                    className="p-3 cursor-pointer hover:bg-muted/50"
                    onClick={() => handleAccountSelect(consumer)}
                  >
                    <div className="flex justify-between items-start">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{`${consumer.firstName} ${consumer.lastName}`}</p>
                          <Badge variant="outline">{consumer.consumerNo}</Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          {consumer.contactNumber && (
                            <div className="flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {consumer.contactNumber}
                            </div>
                          )}
                          {consumer.email && (
                            <div className="flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              {consumer.email}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge
                          variant={
                            consumer.statusDisplay === "ACTIVE"
                              ? "secondary"
                              : "destructive"
                          }
                        >
                          {consumer.statusDisplay}
                        </Badge>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Consumer Metadata - Shows when loaded */}
            {consumerMetadata && (
              <>
                <Separator className="my-4" />
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-green-800">
                    Account Details
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    {consumerMetadata.consumerNo && (
                      <div>
                        <p className="text-muted-foreground">Consumer Number</p>
                        <p className="font-medium">
                          {consumerMetadata.consumerNo}
                        </p>
                      </div>
                    )}
                    {consumerMetadata.firstName &&
                      consumerMetadata.lastName && (
                        <div>
                          <p className="text-muted-foreground">Full Name</p>
                          <p className="font-medium">{`${consumerMetadata.firstName} ${consumerMetadata.lastName}`}</p>
                        </div>
                      )}
                    {consumerMetadata.email && (
                      <div>
                        <p className="text-muted-foreground">Email</p>
                        <p className="font-medium">{consumerMetadata.email}</p>
                      </div>
                    )}
                    {consumerMetadata.contactNumber && (
                      <div>
                        <p className="text-muted-foreground">Contact Number</p>
                        <p className="font-medium">
                          {consumerMetadata.contactNumber}
                        </p>
                      </div>
                    )}
                    {consumerMetadata.categoryDisplay && (
                      <div>
                        <p className="text-muted-foreground">Category</p>
                        <p className="font-medium">
                          {consumerMetadata.categoryDisplay}
                        </p>
                      </div>
                    )}
                    {consumerMetadata.subCategoryDisplay && (
                      <div>
                        <p className="text-muted-foreground">Sub Category</p>
                        <p className="font-medium">
                          {consumerMetadata.subCategoryDisplay}
                        </p>
                      </div>
                    )}
                    {consumerMetadata.serviceAddress && (
                      <div>
                        <p className="text-muted-foreground">Service Address</p>
                        <p className="font-medium">
                          {formatTerritoryAddress(
                            { service: consumerMetadata?.serviceAddress },
                            "service"
                          ) || "Not available"}
                        </p>
                      </div>
                    )}
                    {consumerMetadata.utilityServices &&
                      consumerMetadata.utilityServices.length > 0 && (
                        <div>
                          <p className="text-muted-foreground">
                            Utility Services
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {consumerMetadata.utilityServices.map(
                              (service, index) => (
                                <Badge
                                  key={index}
                                  variant="outline"
                                  className="text-xs"
                                >
                                  {service}
                                </Badge>
                              )
                            )}
                          </div>
                        </div>
                      )}
                  </div>
                </div>
              </>
            )}

            {/* Error state for metadata */}
            {consumerMetaDataQuery.isError && (
              <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
                <AlertTriangle className="h-3 w-3 inline mr-1" />
                Unable to load additional account details
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Selected Account Details - Show when account is selected OR loaded from URL */}
      {selectedAccount && (
        <Card className="border-l-4 border-l-blue-500">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              {consumerId ? "Consumer Details" : "Account Identification"}
              <ContextualHelp
                title={consumerId ? "Consumer Information" : "Account Search"}
                content={
                  consumerId
                    ? "Review the consumer account details before proceeding with payment."
                    : "Search for the customer account using their account number, name, or address. You can search by typing any part of their information."
                }
              />
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Consumer Metadata - Shows when loaded */}
            {consumerMetadata && (
              <>
                <Separator className="my-4" />
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-green-800">
                    Account Details
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    {consumerMetadata.consumerNo && (
                      <div>
                        <p className="text-muted-foreground">Consumer Number</p>
                        <p className="font-medium">
                          {consumerMetadata.consumerNo}
                        </p>
                      </div>
                    )}
                    {consumerMetadata.firstName &&
                      consumerMetadata.lastName && (
                        <div>
                          <p className="text-muted-foreground">Full Name</p>
                          <p className="font-medium">{`${consumerMetadata.firstName} ${consumerMetadata.lastName}`}</p>
                        </div>
                      )}
                    {consumerMetadata.email && (
                      <div>
                        <p className="text-muted-foreground">Email</p>
                        <p className="font-medium">{consumerMetadata.email}</p>
                      </div>
                    )}
                    {consumerMetadata.contactNumber && (
                      <div>
                        <p className="text-muted-foreground">Contact Number</p>
                        <p className="font-medium">
                          {consumerMetadata.contactNumber}
                        </p>
                      </div>
                    )}
                    {consumerMetadata.categoryDisplay && (
                      <div>
                        <p className="text-muted-foreground">Category</p>
                        <p className="font-medium">
                          {consumerMetadata.categoryDisplay}
                        </p>
                      </div>
                    )}
                    {consumerMetadata.subCategoryDisplay && (
                      <div>
                        <p className="text-muted-foreground">Sub Category</p>
                        <p className="font-medium">
                          {consumerMetadata.subCategoryDisplay}
                        </p>
                      </div>
                    )}
                     {consumerMetadata.serviceAddress && (
                      <div>
                        <p className="text-muted-foreground">Service Address</p>
                        <p className="font-medium">
                          {formatTerritoryAddress(
                            { service: consumerMetadata?.serviceAddress },
                            "service"
                          ) || "Not available"}
                        </p>
                      </div>
                    )}
                    {consumerMetadata.utilityServices &&
                      consumerMetadata.utilityServices.length > 0 && (
                        <div>
                          <p className="text-muted-foreground">
                            Utility Services
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {consumerMetadata.utilityServices.map(
                              (service, index) => (
                                <Badge
                                  key={index}
                                  variant="outline"
                                  className="text-xs"
                                >
                                  {service}
                                </Badge>
                              )
                            )}
                          </div>
                        </div>
                      )}
                  </div>
                </div>
              </>
            )}

            {/* Error state for metadata */}
            {consumerMetaDataQuery.isError && (
              <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
                <AlertTriangle className="h-3 w-3 inline mr-1" />
                Unable to load additional account details
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {selectedAccount && (
        <>
          <Separator />

          {/* Dynamic Form */}
          <DynamicForm
            key={`form-${selectedAccount.id}-${latestBillQuery.dataUpdatedAt}-${paymentServicesQuery.dataUpdatedAt}`}
            fields={formFields}
            gridColumns={2}
            responsiveColumns={{ sm: 1, md: 2 }}
            form={form}
            onSubmit={handleSubmit}
            onFieldChange={handleFieldChange}
            config={{
              showSubmitButton: false, // We'll use custom footer
              showResetButton: false,
            }}
          />

          {/* Cash Change Display */}
          {formValues.paymentMethod.toLowerCase() === "cash" &&
            getChangeAmount() > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-blue-600" />
                    <span className="font-medium text-blue-800">
                      {createCreditNote
                        ? "Credit Amount:"
                        : "Change to Give Back:"}
                    </span>
                  </div>
                  <Badge
                    variant="outline"
                    className="bg-blue-50 text-blue-700 border-blue-300 font-bold text-lg"
                  >
                    ${getChangeAmount()}
                  </Badge>
                </div>
                <p className="text-xs text-blue-700 mt-1">
                  {createCreditNote
                    ? "Amount to be credited to customer account"
                    : "Amount to return to customer"}
                </p>
              </div>
            )}

          <Separator />

          {/* Review and Confirm Section */}
          <Card className="border-l-4 border-l-red-500">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Review and Confirm
                <ContextualHelp
                  title="Payment Review"
                  content="Review all payment details before processing. Ensure all information is correct as this cannot be undone."
                />
              </CardTitle>
            </CardHeader>

            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Account Summary */}
                <div className="space-y-4">
                  <h3 className="font-medium">Account Summary</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Customer:</span>
                      <span>{`${selectedAccount.firstName} ${selectedAccount.lastName}`}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Account:</span>
                      <span>{selectedAccount.consumerNo}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Selected Balance:
                      </span>
                      <span>${balanceAmount}</span>
                    </div>
                  </div>
                </div>

                {/* Payment Summary */}
                <div className="space-y-4">
                  <h3 className="font-medium">Payment Summary</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Payment Type:
                      </span>
                      <span>
                        {(Array.isArray(paymentPayTypes)
                          ? paymentPayTypes
                          : []
                        ).find(
                          (pt) =>
                            String(pt.key) === String(formValues.paymentType)
                        )?.value || "Bill Payment"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Payment Received:
                      </span>
                      <span>${formValues.amount}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Payment Method:
                      </span>
                      <span>{selectedPaymentMethod?.name}</span>
                    </div>
                    <div className="flex justify-between font-medium">
                      <span>Total Amount:</span>
                      <span>${Number(formValues.amount) || 0}</span>
                    </div>
                    {selectedPaymentMethod?.name.toLowerCase() === "cash" &&
                      formValues.payment_amount && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            Received Cash:
                          </span>
                          <span>${parseFloat(formValues.payment_amount)}</span>
                        </div>
                      )}
                    {getChangeAmount() > 0 && (
                      <div className="flex justify-between text-blue-600 font-medium">
                        <span>
                          {createCreditNote
                            ? "Credit Note Given:"
                            : "Change Given:"}
                        </span>
                        <span>${getChangeAmount()}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Recorded By:
                      </span>
                      <span>{userProfile?.user?.name}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Dynamic Banner Section */}
              <div className="space-y-4">
                {getOutstandingAmount() > 0 ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-amber-600" />
                        <div>
                          <h4 className="font-medium text-amber-800">
                            Remaining Balance
                          </h4>
                          <p className="text-sm text-amber-700">
                            Remaining balance after payment
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-amber-600">
                          ${getOutstandingAmount()}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : getExcessAmount() > 0 &&
                  selectedPaymentMethod?.name.toLowerCase() === "cash" ? (
                  <div className="bg-green-50 border border-green-200 rounded-md p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                        <div>
                          <h4 className="font-medium text-green-800">
                            Excess Payment
                          </h4>
                          <p className="text-sm text-green-700">
                            Amount exceeding selected balance
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-green-600">
                          ${getExcessAmount()}
                        </p>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-green-200">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="createCreditNote"
                          checked={createCreditNote}
                          onChange={(e) =>
                            setCreateCreditNote(e.target.checked)
                          }
                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                        />
                        <label
                          htmlFor="createCreditNote"
                          className="text-sm font-medium text-green-800"
                        >
                          Create a credit note for excess amount
                        </label>
                      </div>
                      <p className="text-xs text-green-700 mt-1 ml-6">
                        A credit note will be created for ${getExcessAmount()}{" "}
                        that can be applied to future bills
                      </p>
                    </div>
                  </div>
                ) : getChangeAmount() > 0 ? (
                  <div className="bg-green-50 border border-green-200 rounded-md p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                        <div>
                          <h4 className="font-medium text-green-800">
                            Change Amount
                          </h4>
                          <p className="text-sm text-green-700">
                            Change amount from cash payment
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-green-600">
                          ${getChangeAmount()}
                        </p>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-green-200">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="createCreditNote"
                          checked={createCreditNote}
                          onChange={(e) =>
                            setCreateCreditNote(e.target.checked)
                          }
                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                        />
                        <label
                          htmlFor="createCreditNote"
                          className="text-sm font-medium text-green-800"
                        >
                          Create a credit note for change amount
                        </label>
                      </div>
                      <p className="text-xs text-green-700 mt-1 ml-6">
                        A credit note will be created for ${getChangeAmount()}{" "}
                        that can be applied to future bills
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-blue-600" />
                        <div>
                          <h4 className="font-medium text-blue-800">
                            Perfect Balance
                          </h4>
                          <p className="text-sm text-blue-700">
                            Payment matches exactly. No balance due.
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-blue-600">$0.00</p>
                        <p className="text-sm text-blue-600">New Balance</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-4">
                <Button variant="outline" onClick={() => window.history.back()}>
                  Cancel
                </Button>
                <Button
                  onClick={() => handleSubmit(formValues)}
                  disabled={
                    (Number(formValues.expectedAmount) || 0) <= 0 ||
                    !selectedAccount ||
                    createPaymentMutation.isPending
                  }
                  className="bg-primary hover:bg-primary/90"
                >
                  {createPaymentMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Shield className="mr-2 h-4 w-4" />
                      Process Payment
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
